// Mesh/agent tests key shared "target" filters by destination agent id, while traceroute keys targets by IP.

interface TraceTarget {
  target_ip: string;
  target_agent_id?: string | number | null;
}

interface TestFlags {
  isMesh?: boolean;
  isAgentTest?: boolean;
}

function usesAgentTargets(test?: TestFlags | null): boolean {
  return !!(test?.isMesh || test?.isAgentTest);
}

export function sharedTargetsToIps(
  test: TestFlags | null | undefined,
  targets: Array<string | number>,
  allTargets: TraceTarget[] = []
): string[] {
  if (!usesAgentTargets(test)) {
    return targets.map(String);
  }
  const wanted = new Set(targets.map(String));
  const ips = allTargets
    .filter(({ target_agent_id }) => target_agent_id != null && wanted.has(String(target_agent_id)))
    .map(({ target_ip }) => target_ip);
  return [...new Set(ips)];
}

export function ipsToSharedTargets(
  test: TestFlags | null | undefined,
  ips: string[],
  allTargets: TraceTarget[] = []
): string[] {
  if (!usesAgentTargets(test)) {
    return ips;
  }
  const agentByIp = new Map(allTargets.map(({ target_ip, target_agent_id }) => [target_ip, target_agent_id]));
  const ids = ips.map((ip) => agentByIp.get(ip)).filter((id) => id != null);
  return [...new Set(ids.map(String))];
}

export interface SharedSelection {
  agents: Array<string | number>;
  targets: Array<string | number>;
}

function isSameSelection(a: SharedSelection, b: SharedSelection): boolean {
  const key = (values: Array<string | number>) => values.map(String).sort().join('\u0000');
  return key(a.agents) === key(b.agents) && key(a.targets) === key(b.targets);
}

/** Clamp a shared selection to what traceroute can draw. Table view keeps the newest pick. */
export function clampTraceSelection(
  selection: SharedSelection,
  maxItems: number,
  keepNewest = false
): { selection: SharedSelection; truncated: boolean } {
  const clamp = <T>(values: T[]) => (keepNewest ? values.slice(-maxItems) : values.slice(0, maxItems));
  const next = { agents: clamp(selection.agents), targets: clamp(selection.targets) };
  const truncated = next.agents.length < selection.agents.length || next.targets.length < selection.targets.length;
  return { selection: next, truncated };
}

/**
 * Traceroute narrows the shared filters to what it can draw while its tab is open. This keeps the
 * selection from before the first narrowing so it can be handed back when the tab closes. A filter
 * change the user makes in graph view replaces it; table view picks are always temporary.
 */
export class TraceSelectionScope {
  private savedSelection: SharedSelection | null = null;

  private lastWritten: SharedSelection | null = null;

  get saved(): SharedSelection | null {
    return this.savedSelection;
  }

  /** Call before Traceroute narrows the shared filters; the earliest (user's) selection wins. */
  saveBeforeNarrowing(current: SharedSelection): void {
    if (!this.savedSelection) {
      this.savedSelection = current;
    }
  }

  /** Call whenever Traceroute writes the shared filters, so its own writes aren't mistaken for the user's. */
  recordWrite(selection: SharedSelection): void {
    this.lastWritten = selection;
  }

  /** Call on every observed shared filter change. */
  observe(current: SharedSelection, { isTable }: { isTable: boolean }): void {
    const ownWrite = this.lastWritten !== null && isSameSelection(current, this.lastWritten);
    this.lastWritten = null;
    if (!ownWrite && !isTable) {
      this.savedSelection = null;
    }
  }

  /** The selection to restore when the tab closes, if it differs from `current`. */
  takeRestore(current: SharedSelection): SharedSelection | null {
    const saved = this.savedSelection;
    this.savedSelection = null;
    return saved && !isSameSelection(saved, current) ? saved : null;
  }
}
